import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReportAIServiceV2 from './ReportAIServiceV2';
import {
    ReportExecutionResult,
    GlobalExecuteRequest,
    GlobalExecuteResponse,
    GlobalReportDefinitionRequest,
    CompanyReportSettingResponse
} from '@/models/reportAI';

const reportAIService = ReportAIServiceV2.getInstance();

export const REPORT_AI_QUERY_KEYS = {
    DEFINITIONS: 'REPORT_AI_DEFINITIONS',
    EXECUTE: 'REPORT_AI_EXECUTE',
    GLOBAL_DEFINITIONS: 'REPORT_AI_GLOBAL_DEFINITIONS',
    GLOBAL_EXECUTE: 'REPORT_AI_GLOBAL_EXECUTE',
    COMPANY_REPORT_SETTINGS: 'REPORT_AI_COMPANY_REPORT_SETTINGS'
};

export const useReportDefinitions = (companyId: string, enabled = true) =>
    useQuery({
        queryKey: [REPORT_AI_QUERY_KEYS.DEFINITIONS, companyId],
        queryFn: async () => {
            const res = await reportAIService.getDefinitions(companyId);
            return res.data;
        },
        enabled: enabled && !!companyId,
        retry: false,
        staleTime: 60_000
    });

export const useDeleteDefinition = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (reportId: string) =>
            reportAIService.deleteDefinition(reportId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [REPORT_AI_QUERY_KEYS.DEFINITIONS]
            });
        }
    });
};

export const useToggleDefinition = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (reportId: string) =>
            reportAIService.toggleDefinition(reportId),
        onSuccess: (_data, _variables, _context) => {
            queryClient.invalidateQueries({
                queryKey: [REPORT_AI_QUERY_KEYS.DEFINITIONS]
            });
            queryClient.invalidateQueries({
                queryKey: [REPORT_AI_QUERY_KEYS.COMPANY_REPORT_SETTINGS]
            });
        }
    });
};

export const useToggleDraftDefinition = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (reportId: string) =>
            reportAIService.toggleDefinition(reportId, 'draft'),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [REPORT_AI_QUERY_KEYS.DEFINITIONS]
            });
            queryClient.invalidateQueries({
                queryKey: [REPORT_AI_QUERY_KEYS.COMPANY_REPORT_SETTINGS]
            });
        }
    });
};

export const useExecuteReport = (
    reportId: string,
    companyId: string,
    params: Record<string, string | undefined>,
    enabled = false
) =>
    useQuery<ReportExecutionResult>({
        queryKey: [REPORT_AI_QUERY_KEYS.EXECUTE, reportId, companyId, params],
        queryFn: async () => {
            const res = await reportAIService.executeReport(
                reportId,
                companyId,
                params
            );
            return res.data;
        },
        enabled: enabled && !!reportId && !!companyId,
        retry: false
    });

export const useGlobalReportDefinitions = (enabled = true) =>
    useQuery({
        queryKey: [REPORT_AI_QUERY_KEYS.GLOBAL_DEFINITIONS],
        queryFn: async () => {
            const res = await reportAIService.getGlobalDefinitions();
            return res.data;
        },
        enabled,
        retry: false,
        staleTime: 60_000
    });

export const useExecuteGlobalReport = (
    reportId: string,
    body: GlobalExecuteRequest,
    enabled: boolean
) =>
    useQuery<GlobalExecuteResponse>({
        queryKey: [
            REPORT_AI_QUERY_KEYS.GLOBAL_EXECUTE,
            reportId,
            body.companyIds,
            body.parameters
        ],
        queryFn: async () => {
            const res = await reportAIService.executeGlobalReport(
                reportId,
                body
            );
            return res.data;
        },
        enabled: enabled && !!reportId,
        retry: false
    });

export const useDeleteGlobalDefinition = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (reportId: string) =>
            reportAIService.deleteGlobalDefinition(reportId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [REPORT_AI_QUERY_KEYS.GLOBAL_DEFINITIONS]
            });
        }
    });
};

export const useCreateGlobalDefinition = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: GlobalReportDefinitionRequest) =>
            reportAIService.createGlobalDefinition(body),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [REPORT_AI_QUERY_KEYS.GLOBAL_DEFINITIONS]
            });
        }
    });
};

export const useCompanyReportSettings = (companyId: string, enabled = true) =>
    useQuery<CompanyReportSettingResponse[]>({
        queryKey: [REPORT_AI_QUERY_KEYS.COMPANY_REPORT_SETTINGS, companyId],
        queryFn: async () => {
            const res = await reportAIService.getCompanyReportSettings(
                companyId
            );
            return res.data;
        },
        enabled: enabled && !!companyId,
        retry: false,
        staleTime: 60_000
    });
